import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Mail, Phone, KeyRound } from "lucide-react";
import { SetPasswordDialog, ImpersonateButton } from "../dashboard/UserAdminActions";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";
import MobileRegistrations from "./MobileRegistrations";
import EditableCell from "../dashboard/EditableCell";

const STATUS_OPTS = [{ value: "active", label: "Attivo" }, { value: "suspended", label: "Sospeso" }];

const emptyAgent = { name: "", email: "", password: "", commission_rate: 8, discount_pct: 0, phone: "", zone: "" };

export default function MobileAgents() {
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(emptyAgent);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pwdUser, setPwdUser] = useState(null);

  const load = () => api.get("/agents").then(({ data }) => setAgents(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openForm = (a) => { setForm(a ? { ...a, password: "" } : emptyAgent); setEditId(a?.id || null); setOpen(true); };
  const save = async () => {
    try {
      if (editId) {
        const payload = { name: form.name, commission_rate: Number(form.commission_rate), discount_pct: Number(form.discount_pct) || 0, phone: form.phone, zone: form.zone };
        if (form.password) payload.password = form.password;
        await api.put(`/agents/${editId}`, payload);
      } else {
        await api.post("/agents", { ...form, commission_rate: Number(form.commission_rate), discount_pct: Number(form.discount_pct) || 0 });
      }
      toast.success("Agente salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'agente ${a.name}?`)) return;
    await api.delete(`/agents/${a.id}`); toast.success("Eliminato"); load();
  };
  const sendCommissions = async (id) => {
    try { const { data } = await api.post(`/agents/${id}/send-commissions`); toast.success(`Email inviata · ${eur(data.total_commissions)}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const list = agents.filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || (a.zone || "").toLowerCase().includes(q.toLowerCase()));
  const inline = (a, field) => async (v) => {
    try { await api.put(`/registrations/${a.id}`, { [field]: v }); toast.success("Salvato"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cell = (a, field, extra = {}) => <EditableCell value={a[field] ?? (field === "status" ? "active" : field.endsWith("_pct") || field === "commission_rate" ? 0 : "")} onSave={inline(a, field)} testId={`mobile-edit-${field}-${a.id}`} {...extra} />;

  return (
    <div data-testid="mobile-agents">
      <MobileRegistrations />
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-2xl font-semibold text-[#0B2A45]">Agenti <span className="text-stone-400 text-base">({agents.length})</span></h1>
        <Button onClick={() => openForm(null)} data-testid="mobile-add-agent-btn" className="bg-[#0F4C81] text-white rounded-full h-10 px-4"><Plus className="w-4 h-4 mr-1" />Nuovo</Button>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o zona…" className="h-11 mb-1" data-testid="mobile-agents-search" />
      <p className="text-[11px] text-stone-400 mb-4">Tocca un dato per modificarlo: si salva subito.</p>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-stone-400 py-10">Nessun agente</p>}
        {list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`mobile-agent-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0B2A45]">{cell(a, "name")}</div>
                <div className="text-xs text-stone-500">{cell(a, "email", { type: "email" })}</div>
                <div className="text-xs text-stone-400 grid grid-cols-2 gap-1 mt-1">
                  <div><span className="text-[10px] uppercase">Zona</span>{cell(a, "zone")}</div>
                  <div><span className="text-[10px] uppercase">Tel.</span>{cell(a, "phone", { type: "tel" })}</div>
                </div>
              </div>
              <div className="text-right shrink-0 w-24">
                <div className="font-mono text-sm font-semibold text-[#15803D] flex items-center justify-end">{cell(a, "commission_rate", { type: "number", className: "text-right" })}%</div>
                <div className="text-[10px] uppercase tracking-wide text-stone-400">provv.</div>
                <div className="font-mono text-xs text-stone-500 flex items-center justify-end mt-1">{cell(a, "discount_pct", { type: "number", className: "text-right" })}%</div>
                <div className="text-[10px] uppercase tracking-wide text-stone-400">sconto</div>
                <div className="mt-1">{a.status === "pending" ? <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">In attesa</span> : cell(a, "status", { options: STATUS_OPTS })}</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
              {a.phone && <a href={`tel:${a.phone}`} className="flex-1 h-10 rounded-xl bg-stone-50 flex items-center justify-center text-stone-600" data-testid={`mobile-call-agent-${a.id}`}><Phone className="w-4 h-4" /></a>}
              <button onClick={() => sendCommissions(a.id)} className="flex-1 h-10 rounded-xl bg-stone-50 flex items-center justify-center text-[#15803D]" data-testid={`mobile-send-commissions-${a.id}`}><Mail className="w-4 h-4" /></button>
              <button onClick={() => openForm(a)} className="flex-1 h-10 rounded-xl bg-stone-50 flex items-center justify-center text-[#0F4C81]" data-testid={`mobile-edit-agent-${a.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(a)} className="flex-1 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-600" data-testid={`mobile-delete-agent-${a.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => setPwdUser(a)} className="flex-1 h-10 rounded-xl bg-stone-50 text-xs font-semibold text-[#0F4C81] flex items-center justify-center gap-1" data-testid={`mobile-set-pwd-${a.id}`}>
                <KeyRound className="w-4 h-4" />{a.admin_set_password ? <span className="font-mono">{a.admin_set_password}</span> : "Imposta password"}
              </button>
              <ImpersonateButton user={{ ...a, status: a.status || "active" }} compact className="flex-1 h-10" />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Modifica" : "Nuovo"} agente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="mobile-agent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Email</Label><Input type="email" disabled={!!editId} data-testid="mobile-agent-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Password {editId && <span className="text-xs text-stone-400">(vuoto = non modificare)</span>}</Label><Input type="password" data-testid="mobile-agent-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Provvigione %</Label><Input type="number" step="0.5" inputMode="decimal" data-testid="mobile-agent-commission-input" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Sconto %</Label><Input type="number" step="0.5" inputMode="decimal" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="mt-1 h-11" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Zona</Label><Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Telefono</Label><Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="mobile-save-agent-btn" className="bg-[#0F4C81] text-white rounded-full h-11 w-full">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {pwdUser && <SetPasswordDialog user={pwdUser} onClose={() => setPwdUser(null)} onDone={load} />}
    </div>
  );
}
